import { useMemo, useCallback } from 'react';

import type {
  CascaderOption,
  CascaderSearchFilter,
  CascaderSearchSort,
  CascaderSubmenu,
} from '../types';
import {
  isSubmenu,
  addPathsToOptions,
  flattenOptions,
  getOptionsAtLevel,
} from '../util';

export const baseSearchFilter: CascaderSearchFilter = (inputValue, labels) => {
  const words = inputValue
    .toLowerCase()
    .split(' ')
    .filter((word) => word.length > 0);
  if (words.length === 0) return true;

  const splitLabels = labels.map((label) => label.split(' ')).flat();

  const { matchedWords } = splitLabels.reduce(
    (acc, optionLabel) => {
      if (acc.matchedWords >= words.length) return acc;
      const label = optionLabel.toLowerCase();
      if (label.includes(words[acc.matchedWords])) {
        return { ...acc, matchedWords: acc.matchedWords + 1 };
      }
      return acc;
    },
    { matchedWords: 0 },
  );

  return matchedWords === words.length;
};

export const baseSearchSort: CascaderSearchSort = (a, b) =>
  a.fullPath.length - b.fullPath.length;

export interface UseCascaderComputedOptions<OptionType extends CascaderOption> {
  options: CascaderSubmenu<OptionType>;
  inputValue: string;
  selectedPath: string[];
  cursorPath: number[];
  searchFilter?: CascaderSearchFilter;
  searchSort?: CascaderSearchSort;
}

export const useCascaderComputedValues = <OptionType extends CascaderOption>({
  options,
  inputValue,
  selectedPath,
  cursorPath,
  searchFilter = baseSearchFilter,
  searchSort = baseSearchSort,
}: UseCascaderComputedOptions<OptionType>) => {
  const root = useMemo(() => ({ ...options, value: 'root' }), [options]);

  const rootGroups = options.children;

  const rootOptions = useMemo(
    () => options.children.flatMap((group) => group.children),
    [options],
  );

  const rootOptionsWithPaths = useMemo(
    () => addPathsToOptions(rootOptions),
    [rootOptions],
  );

  const flatOptionsWithSubmenus = useMemo(
    () => flattenOptions(rootOptionsWithPaths),
    [rootOptionsWithPaths],
  );

  const flatOptions = useMemo(
    () => flatOptionsWithSubmenus.filter((opt) => !isSubmenu(opt)),
    [flatOptionsWithSubmenus],
  );

  const filteredOptions = useMemo(
    () =>
      flatOptions
        .filter((opt) => searchFilter(inputValue, opt.fullLabels))
        .sort(searchSort),
    [flatOptions, inputValue, searchFilter, searchSort],
  );

  const getOptionAtPath = useCallback(
    (path: string[]) =>
      flatOptionsWithSubmenus.find(
        (opt) => opt.fullPath.join('/') === path.join('/'),
      ),
    [flatOptionsWithSubmenus],
  );

  const selectedOption = useMemo(
    () => getOptionAtPath(selectedPath),
    [selectedPath, getOptionAtPath],
  );

  const cursorOption: OptionType | null = useMemo(() => {
    if (cursorPath.length > 0) {
      const activeLevel = cursorPath.length - 1;
      const currentOptions = getOptionsAtLevel(
        rootOptions,
        cursorPath,
        activeLevel,
      );
      const currentIndex = cursorPath[activeLevel];
      return currentOptions[currentIndex] ?? null;
    }
    return null;
  }, [cursorPath, rootOptions]);

  const getOptionsAtLevelCallback = useCallback(
    (level: number): OptionType[] =>
      getOptionsAtLevel(rootOptions, cursorPath, level),
    [rootOptions, cursorPath],
  );

  return {
    root,
    rootGroups,
    rootOptions,
    rootOptionsWithPaths,
    flatOptions,
    filteredOptions,
    selectedOption,
    cursorOption,
    getOptionAtPath,
    getOptionsAtLevel: getOptionsAtLevelCallback,
  };
};
